import { Core, Decorative } from "@faire/design-tokens";
import { TrustPilotWidget } from "@faire/retailer-visitor-shared/components/TestimonialCarousel/TrustPilotWidget";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { getTrustpilotRating } from "@faire/retailer-visitor-shared/serialized-data/getTrustpilotRating";
import {
  CASE_STUDIES,
  DEFAULT_CASE_STUDY,
  TRUST_PILOT_MINIMUM_RATING,
} from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isEnumKey } from "@faire/web--source/common/typescriptUtils";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContent } from "@faire/web--source/ui/ResponsiveContent";
import { trackBrandLeadsCaseStudySectionLinkedPdfClick } from "@faire/web-api--source/events/brandLeads/click/caseStudySectionLinkedPdf";
import { trackBrandLeadsCaseStudySectionView } from "@faire/web-api--source/events/brandLeads/view/caseStudySection";
import { trackBrandLeadsTrustPilotBadgeView } from "@faire/web-api--source/events/brandLeads/view/trustPilotBadge";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled from "styled-components";

interface ICaseStudyCardProps {
  brandInfo: string;
  title: string;
  image: string;
  pdf: string;
}

const BrandsLandingPageCaseStudiesBase: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { retailerCountry } = useNationality();

  const allCaseStudies = CASE_STUDIES(strictLocalize);
  const caseStudy =
    retailerCountry && isEnumKey(allCaseStudies, retailerCountry)
      ? allCaseStudies[retailerCountry]! // We know that the key is valid from isEnumKey check above
      : DEFAULT_CASE_STUDY(strictLocalize);

  useEffect(() => {
    trackBrandLeadsCaseStudySectionView();
  }, []);

  const viewRef = useDoActionWhenVisible(trackBrandLeadsTrustPilotBadgeView);
  const FAIRE_TRUSTPILOT_RATING = getTrustpilotRating();

  return (
    <Wrapper>
      <ResponsiveContent
        mobile={
          <>
            <HorizontalLine />
            <Spacer height="4x" />
          </>
        }
        desktop={
          <Spacer
            height={{
              mobileAndAbove: 0,
              tabletAndAbove: "8x",
              desktopAndAbove: "10x",
            }}
          />
        }
      />

      <Title
        variant={{
          mobileAndAbove: "pageHeaderSerifRegular",
          tabletAndAbove: "displayMSerifRegular",
          desktopAndAbove: "displayLSerifRegular",
        }}
      >
        {caseStudy.sectionTitle}
      </Title>
      <Spacer
        height={{
          mobileAndAbove: "2x",
          tabletAndAbove: "5x",
        }}
      />
      <ResponsiveContent
        mobile={
          <MobileCaseStudyCard
            brandInfo={caseStudy.brandInfo}
            title={caseStudy.title}
            image={caseStudy.mobileImage}
            pdf={caseStudy.mobilePdf}
          />
        }
        desktop={
          <DesktopCaseStudyCard
            brandInfo={caseStudy.brandInfo}
            title={caseStudy.title}
            image={caseStudy.desktopImage}
            pdf={caseStudy.desktopPdf}
          />
        }
      />
      <Spacer
        height={{
          mobileAndAbove: "5x",
          tabletAndAbove: "7x",
        }}
      />
      <ResponsiveContent
        mobile={null}
        desktop={
          FAIRE_TRUSTPILOT_RATING >= TRUST_PILOT_MINIMUM_RATING ? (
            <>
              <div ref={viewRef}>
                <TrustPilotWidget rating={FAIRE_TRUSTPILOT_RATING} />
              </div>
              <Spacer
                height={{
                  mobileAndAbove: "7x",
                  desktopAndAbove: "10x",
                }}
              />
            </>
          ) : (
            <Spacer
              height={{
                mobileAndAbove: 0,
                desktopAndAbove: "3x",
              }}
            />
          )
        }
      />
    </Wrapper>
  );
};

export const BrandsLandingPageCaseStudies = observer(
  BrandsLandingPageCaseStudiesBase
);

const MobileCaseStudyCard: React.FC<ICaseStudyCardProps> = ({
  brandInfo,
  title,
  image,
  pdf,
}) => {
  const isSSR = useIsSSR();
  return (
    <MobileCaseStudyWrapper>
      <MobileCaseStudyImage
        src={optimizeImageUrl({ url: image }, isSSR, {
          width: 312,
          height: 146,
          resizeMethod: "fit",
        })}
      />
      <Spacer width="3x" />
      <CaseStudyBody {...{ brandInfo, title, pdf }} />
      <Spacer width="2x" />
    </MobileCaseStudyWrapper>
  );
};

const DesktopCaseStudyCard: React.FC<ICaseStudyCardProps> = ({
  brandInfo,
  title,
  image,
  pdf,
}) => {
  const isSSR = useIsSSR();
  return (
    <DesktopCaseStudyWrapper>
      <DesktopCaseStudyImage
        src={optimizeImageUrl({ url: image }, isSSR, {
          width: 458,
          height: 332,
          resizeMethod: "fit",
        })}
      />
      <Spacer
        width={{
          mobileAndAbove: "6x",
          desktopAndAbove: "8x",
        }}
      />
      <CaseStudyBody {...{ brandInfo, title, pdf }} />
      <Spacer width="2x" />
    </DesktopCaseStudyWrapper>
  );
};

const CaseStudyBody: React.FC<{
  brandInfo: string;
  title: string;
  pdf: string;
}> = ({ brandInfo, title, pdf }) => (
  <CaseStudyBodyWrapper>
    <Typography
      color={Core.text.subdued}
      variant={{
        mobileAndAbove: "labelSansRegular",
        tabletAndAbove: "paragraphSansRegular",
        desktopAndAbove: "subheadingSansRegular",
      }}
    >
      {brandInfo}
    </Typography>
    <Spacer
      height={{
        mobileAndAbove: "1x",
        tabletAndAbove: "2x",
      }}
    />
    <Typography
      variant={{
        mobileAndAbove: "paragraphSansMedium",
        tabletAndAbove: "subheadingSansRegular",
        desktopAndAbove: "displaySSansRegular",
      }}
    >
      {title}
    </Typography>
    <Spacer
      height={{
        mobileAndAbove: "2x",
        tabletAndAbove: "3x",
      }}
    />
    <EmbeddedLink
      linkTo={pdf}
      target="_blank"
      onClick={() => {
        trackBrandLeadsCaseStudySectionLinkedPdfClick();
      }}
    >
      <Typography variant="paragraphSansRegular">
        <StrictLocalMsg
          defaultMessage="Read the Case Study"
          description={{ text: "link that redirects to the case study pdf" }}
        />
      </Typography>
      <StyledArrow direction="right" />
    </EmbeddedLink>
  </CaseStudyBodyWrapper>
);

const Wrapper = styled(Column).attrs({ align: "center" })`
  width: 100%;
  ${responsiveMediaQueries.mobile} {
    background-color: ${Core.surface.inverse};
    padding: ${getSpacing(0, "3x")};
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    background-color: ${Decorative.tint.neutral};
  }
`;

const Title = styled(Typography)`
  ${responsiveMediaQueries.mobile} {
    text-align: left;
    width: 100%;
  }
  ${responsiveMediaQueries.tabletAndAbove} {
    width: 824px;
    text-align: center;
  }
`;

const DesktopCaseStudyWrapper = styled(Row).attrs({
  align: "center",
})`
  background-color: ${Core.surface.inverse};
  padding: ${getSpacing("4x")};

  ${responsiveMediaQueries.tablet} {
    max-width: 760px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    max-width: 980px;
  }
`;

const MobileCaseStudyWrapper = styled(Column).attrs({
  align: "center",
})`
  border: 1px solid ${Core.border.muted};
  border-radius: 4px;
  max-width: 312px;
`;

const DesktopCaseStudyImage = styled.img`
  border-radius: 4px;
  ${responsiveMediaQueries.tablet} {
    width: 320px;
    height: 232px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    width: 458px;
    height: 332px;
  }
`;

const MobileCaseStudyImage = styled.img`
  border-radius: 4px 4px 0 0;
  width: 312px;
  height: 146px;
`;

const CaseStudyBodyWrapper = styled(Column)`
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "3x")};
  }
`;

const StyledArrow = styled(Arrow)`
  color: ${Core.text.default};
  height: 20px;
  width: 16px;
  margin-left: 4px;
`;
